import { useMemo, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { fmtCreator } from "../../lib/format";
import { ColFilter, MultiChecks, TextFilter } from "../ColFilters";

const cellText = (it, c) => String(c.render ? c.render(it) : (it[c.key] ?? "—"));
const cellValues = (it, c) => (c.values ? c.values(it).map(String) : [cellText(it, c)]);
const TH = "px-3 py-2.5 text-xs uppercase tracking-wider text-slate-600 font-semibold";

// kolon başlığı filtreleri: 15+ farklı değerde metin araması, aksi halde çoklu seçim
function useColumnFilters(items, columns) {
  const [colFilters, setColFilters] = useState({});
  const colOpts = useMemo(() => {
    const m = {};
    columns.forEach((c) => { m[c.key] = [...new Set(items.flatMap((it) => cellValues(it, c)))].sort((a, b) => a.localeCompare(b, "tr")); });
    return m;
  }, [items, columns]);
  const visible = useMemo(() => items.filter((it) => columns.every((c) => {
    const f = colFilters[c.key];
    if (f == null || f === "") return true;
    const txt = cellText(it, c);
    if (typeof f === "string") return txt.toLocaleLowerCase("tr").includes(f.toLocaleLowerCase("tr"));
    return !f.length || cellValues(it, c).some((v) => f.includes(v));
  })), [items, colFilters, columns]);
  return { colFilters, setColFilters, colOpts, visible };
}

function HeaderCell({ c, opts, f, setFilter, testid }) {
  const useText = opts.length > 15;
  const active = useText ? !!f : !!(f && f.length);
  return (
    <th className={`${TH} ${c.align === "right" ? "text-right" : "text-left"}`}>
      {c.label}
      <ColFilter testid={testid} active={active} align={c.align === "right" ? "right" : "left"}>
        {useText
          ? <TextFilter value={f || ""} onChange={setFilter} testid={`${testid}-input`} />
          : <MultiChecks options={opts.map((v) => ({ value: v, label: v }))} values={f || []} onChange={setFilter} testid={testid} />}
      </ColFilter>
    </th>
  );
}

function Row({ it, columns, showCreator, userLabels, onEdit, onRemove, testidPrefix, readOnly, editing, editForm, colSpan }) {
  return (
    <>
    <tr className={`border-t border-slate-100 ${editing ? "bg-orange-50" : ""}`} data-flash-id={it.id} data-testid={`${testidPrefix}-row-${it.id}`}>
      {columns.map((c) => (
        <td key={c.key} className={`px-3 py-2 text-xs ${c.align === "right" ? "numeric-cell" : ""}`}>
          {c.render ? c.render(it) : it[c.key]}
        </td>
      ))}
      {showCreator && <td className="px-3 py-2 text-[11px] text-slate-500 whitespace-nowrap" data-testid={`${testidPrefix}-creator-${it.id}`}>{fmtCreator(it.created_by, it.created_at, userLabels)}</td>}
      {!readOnly && <td className="px-3 py-2 text-right whitespace-nowrap">
        <button onClick={() => onEdit(it)} className={`mr-2 ${editing ? "text-orange-600" : "text-slate-600 hover:text-slate-900"}`} title={editing ? "Düzenleme açık — ESC ile kapat" : "Düzenle"} data-testid={`${testidPrefix}-edit-${it.id}`}><Pencil className="w-3.5 h-3.5" /></button>
        <button onClick={() => onRemove(it.id)} className="text-red-700 hover:text-red-900" data-testid={`${testidPrefix}-delete-${it.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
      </td>}
    </tr>
    {editing && editForm && <tr data-testid={`${testidPrefix}-inline-edit-${it.id}`}><td colSpan={colSpan} className="p-2 bg-orange-50/60 border-t border-orange-200">{editForm}</td></tr>}
    </>
  );
}

// CrudTable listesi: başlık filtreleri + satırlar + Oluşturan kolonu; düzenlenen satırın hemen altında form (editingId + editForm)
export default function CrudTableView({ items, columns, userLabels, onEdit, onRemove, testidPrefix, readOnly = false, editingId = null, editForm = null }) {
  const showCreator = items.some((it) => it.created_by);
  const { colFilters, setColFilters, colOpts, visible } = useColumnFilters(items, columns);
  const colSpan = columns.length + (readOnly ? 0 : 1) + (showCreator ? 1 : 0);
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <table className="w-full tbl">
        <thead className="bg-slate-50">
          <tr>
            {columns.map((c) => (
              <HeaderCell key={c.key} c={c} opts={colOpts[c.key] || []} f={colFilters[c.key]} testid={`${testidPrefix}-hf-${c.key}`}
                setFilter={(v) => setColFilters({ ...colFilters, [c.key]: v })} />
            ))}
            {showCreator && <th className={`${TH} text-left`}>Oluşturan</th>}
            {!readOnly && <th className="px-3 py-2.5 text-right"></th>}
          </tr>
        </thead>
        <tbody>
          {visible.map((it) => <Row key={it.id} it={it} columns={columns} showCreator={showCreator} userLabels={userLabels} onEdit={onEdit} onRemove={onRemove} testidPrefix={testidPrefix} readOnly={readOnly} editing={editingId === it.id} editForm={editForm} colSpan={colSpan} />)}
          {visible.length === 0 && <tr><td colSpan={colSpan} className="py-8 text-center text-sm text-slate-500">{items.length === 0 ? "Kayıt yok" : "Filtrelerle eşleşen kayıt yok"}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
